import React, { useEffect, useState } from 'react';
import { ScheduleContext } from './ScheduleContextValue';
import { SCHEDULE as STATIC_SCHEDULE, flattenSessions } from '../data/schedule';
import { supabase, hasSupabase } from './supabase';

// Shape produced for both DB rows and the static fallback so screens don't care:
//   session: { type:'session', kind, id, num, title, time, duration, day, startsAt, endsAt, desc, prompt }
//   break:   { type:'break',  time, label }
//   note:    { type:'note',   time, label }

export function ScheduleProvider({ children }) {
  const [schedule, setSchedule] = useState(STATIC_SCHEDULE);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('static');

  useEffect(() => {
    if (!hasSupabase) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    supabase
      .from('sessions')
      .select('*')
      .order('sort_order', { ascending: true })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data || data.length === 0) {
          setLoading(false);
          return;
        }
        setSchedule(overlayDbOnStatic(data.map(rowToItem)));
        setSource('db');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Tick every second so derivations (current/next/countdown) stay fresh.
  // We deliberately don't memoize the context value below — every tick must
  // produce a new value so consumers see secondsRemaining drop.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const derived = deriveCurrent(schedule, Date.now());
  const value = { schedule, loading, source, ...derived };

  return <ScheduleContext.Provider value={value}>{children}</ScheduleContext.Provider>;
}

// ────────────────────────────────────────────────────────────────────────────
// helpers
// ────────────────────────────────────────────────────────────────────────────

function rowToItem(row) {
  const start = new Date(row.starts_at);
  const end = new Date(row.ends_at);
  const durMin = Math.round((end - start) / 60000);
  return {
    type: 'session',
    kind: row.kind || 'session',
    id: row.id,
    num: row.num_label,
    title: row.title,
    desc: row.description || '',
    prompt: row.ai_prompt || null,
    day: row.day,
    startsAt: row.starts_at,
    endsAt: row.ends_at,
    time: `${formatClock(start, end)}`,
    duration: formatDuration(durMin),
  };
}

function formatClock(start, end) {
  const s = formatOne(start);
  const e = formatOne(end);
  // If start and end share AM/PM, drop the suffix from start (e.g. "8:00 – 8:30 AM").
  const sMeridiem = s.slice(-2);
  const eMeridiem = e.slice(-2);
  if (sMeridiem === eMeridiem) return `${s.slice(0, -3)} – ${e}`;
  return `${s} – ${e}`;
}

function formatOne(d) {
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Los_Angeles',
  });
}

function formatDuration(min) {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (m === 0) return `${h} hr`;
  return `${h} hr ${m} min`;
}

// The static file is the "skeleton" — break/note rows live there exclusively
// and stay in their original chronological position. For each row that has an
// id matching a DB row, replace it with the DB version (preserving any
// static-only fields like `time` and `duration` if the DB row didn't override
// them via rowToItem).
function overlayDbOnStatic(dbItems) {
  const dbById = new Map();
  for (const item of dbItems) dbById.set(item.id, item);

  const result = { 0: [], 1: [], 2: [] };
  const used = new Set();

  // 1. Walk the static skeleton, overlaying DB rows where ids match.
  for (const d of [0, 1, 2]) {
    const staticDay = STATIC_SCHEDULE[d] || [];
    for (const staticItem of staticDay) {
      if (staticItem.id && dbById.has(staticItem.id)) {
        result[d].push({ ...staticItem, ...dbById.get(staticItem.id) });
        used.add(staticItem.id);
      } else {
        result[d].push(staticItem);
      }
    }
  }

  // 2. Any DB row without a matching static id (admin-added or test session)
  //    gets prepended to its day so it's visible. The Home countdown uses
  //    `deriveCurrent` independent of array order, so position only matters
  //    for the Schedule tab.
  for (const item of dbItems) {
    if (used.has(item.id)) continue;
    if (item.day in result) {
      result[item.day] = [item, ...result[item.day]];
    }
  }

  return result;
}

function deriveCurrent(schedule, now) {
  const all = flattenSessions(schedule);
  const current =
    all.find((s) => {
      const start = new Date(s.startsAt).getTime();
      const end = new Date(s.endsAt).getTime();
      return now >= start && now < end;
    }) || null;
  const next = all.find((s) => new Date(s.startsAt).getTime() > now) || null;

  let secondsRemaining = 0;
  let totalSeconds = 0;
  if (current) {
    const start = new Date(current.startsAt).getTime();
    const end = new Date(current.endsAt).getTime();
    totalSeconds = Math.max(1, Math.round((end - start) / 1000));
    secondsRemaining = Math.max(0, Math.round((end - now) / 1000));
  }

  const day = current?.day ?? next?.day ?? 1;
  return { current, next, secondsRemaining, totalSeconds, day };
}
